import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { apiPost } from '../../lib/api_v2';
import { cn } from '../../lib/utils';
import { openDirectMessageForWorkspaceProfile } from '../../lib/directMessageNavigation';
import { storeWidgetRuntimeTitle } from '../../lib/widgetRuntimeTitles';
import { createViewerHandshake } from '../../lib/widgetViewerHandshake';

import SpinnerIcon from '../../../assets/images/spinner.svg';

const DEFAULT_WIDGET_EMBED_HEIGHT = 50;
const MAX_WIDGET_EMBED_HEIGHT = 4096;
const VIEWER_SESSION_REFRESH_SKEW_MS = 30_000;
// Fixed height of the collapsed (title-only) bar. Must match
// COLLAPSED_WIDGET_EMBED_HEIGHT in the widget package (resize-bridge.ts / WidgetShell).
const COLLAPSED_WIDGET_EMBED_HEIGHT = 56;
const COLLAPSED_PREF_PREFIX = 'output:widget-collapsed:';

// Seed height shown before the widget posts its own size. A collapsed widget seeds
// straight to the fixed collapsed height (read synchronously from localStorage so the
// first paint is already collapsed); otherwise we clamp the preview height into the
// safe range as a placeholder until the first `resize` arrives.
const seedEmbedHeight = (height, collapsed) => {
  if (collapsed) return COLLAPSED_WIDGET_EMBED_HEIGHT;

  const numericHeight = Number(height);
  if (!Number.isFinite(numericHeight) || numericHeight <= 0) return DEFAULT_WIDGET_EMBED_HEIGHT;

  return Math.min(Math.ceil(numericHeight), MAX_WIDGET_EMBED_HEIGHT);
};

// Heights arrive via postMessage from a sandboxed, possibly AI-generated iframe, so this
// is a trust-boundary guard (finite, positive, capped). We honor whatever valid
// content height the widget sends.
// Returns null for garbage so the caller keeps the last good height.
const clampSafeEmbedHeight = (height) => {
  const numericHeight = Number(height);
  if (!Number.isFinite(numericHeight) || numericHeight <= 0) return null;

  return Math.min(Math.ceil(numericHeight), MAX_WIDGET_EMBED_HEIGHT);
};

const readCollapsedPref = (widgetId) => {
  if (!widgetId) return false;
  try {
    return window.localStorage.getItem(`${COLLAPSED_PREF_PREFIX}${widgetId}`) === '1';
  } catch {
    return false;
  }
};

const writeCollapsedPref = (widgetId, collapsed) => {
  if (!widgetId) return;
  try {
    if (collapsed) window.localStorage.setItem(`${COLLAPSED_PREF_PREFIX}${widgetId}`, '1');
    else window.localStorage.removeItem(`${COLLAPSED_PREF_PREFIX}${widgetId}`);
  } catch {
    // Ignore storage failures (private mode, quota); collapse still works for the session.
  }
};

const logViewerSessionFetchFailure = (error) => console.warn('viewer session fetch failed', error);

// Seeds the iframe's initial collapsed state so its first paint matches the seeded
// container height. Hash (not query) so it never varies the Worker request/caching.
const appendCollapsedHash = (src) => (src.includes('#') ? `${src}&collapsed` : `${src}#collapsed`);

const WidgetEmbed = React.memo(function WidgetEmbed({
  title,
  src,
  height = DEFAULT_WIDGET_EMBED_HEIGHT,
  className,
  agentChatId,
  workspaceId,
  widgetId,
  viewerSessionEnabled = false,
  requiresViewer = false,
  viewerUnavailableReason = null,
  viewerSessionFetcher = null,
  onDeleteRequest = null,
  canMoveUp = false,
  canMoveDown = false,
  onMoveRequest = null,
  lockHeight = false,
}) {
  // The src whose load event has fired; isLoaded derives from it. A src change resets
  // the spinner by derivation, a widget-id change alone (same document — no new load
  // event coming) can never strand a loaded iframe behind it, and a load that fired
  // while the subtree was hidden survives reveal untouched.
  const [loadedSrc, setLoadedSrc] = useState(null);
  const isLoaded = loadedSrc === src;
  const [embedHeight, setEmbedHeight] = useState(() => seedEmbedHeight(height, readCollapsedPref(widgetId)));
  // Stored preview height only seeds the embed before the widget posts its own size.
  // Hold the latest value in a ref so the reset effect can reseed on a new src/widget
  // without re-running on a metadata-only height change (which would hide the loaded
  // iframe and clobber a live `output.widget.resize` height).
  const latestHeightRef = useRef(height);
  latestHeightRef.current = height;
  // Ref (not a dep) so toggling edit mode never re-runs the message-listener
  // effect; the handler just consults the current value.
  const lockHeightRef = useRef(lockHeight);
  lockHeightRef.current = lockHeight;
  const [isPointerGuardActive, setIsPointerGuardActive] = useState(false);
  const iframeRef = useRef(null);
  const iframeIdentity = `${widgetId || ''}\u0000${src || ''}`;
  const committedIframeIdentityRef = useRef(iframeIdentity);
  const committedSrcRef = useRef(src);
  const viewerHandshakeRef = useRef(null);
  const windowMessageHandlerRef = useRef(null);
  const directMessageRequestRef = useRef(null);
  // Four behaviors are folded into three derived booleans so the rest of the component
  // doesn't have to re-derive the matrix. Source of truth is `docs/widget-runtime-auth.md`
  // "Behavior matrix":
  //   - viewer_session_enabled=false                 → silent (no booleans set; the iframe
  //                                                    doesn't know the postMessage protocol)
  //   - enabled=true,  viewer available              → shouldFetchViewerSession
  //   - enabled=true,  viewer absent, !required      → shouldPostViewerUnavailable
  //   - enabled=true,  viewer absent,  required      → shouldBlockEmbed (don't mount iframe)
  // Treating "no viewer" specially is what lets us avoid a guaranteed-reject API call for
  // shadow/no-profile viewers while still keeping the iframe contract deterministic
  // for known no-viewer states. A viewer-capable widget gets session OR unavailable;
  // transient acquisition failures degrade explicitly and continue recovery in the
  // controller's bounded background lane.
  const viewerAvailable = !viewerUnavailableReason;
  const shouldFetchViewerSession = viewerSessionEnabled && viewerAvailable;
  const shouldPostViewerUnavailable = viewerSessionEnabled && !viewerAvailable;
  const shouldBlockEmbed = requiresViewer && !viewerAvailable;
  const shouldPostParentActions = Boolean(onMoveRequest);
  // Ref mirrors the state so the global pointerdown listener can no-op without scheduling a render — with N WidgetEmbeds on screen, every press would otherwise fan out to N setState calls.
  const isPointerGuardActiveRef = useRef(false);
  const isCommittedIframeIdentity = useCallback(() => committedIframeIdentityRef.current === iframeIdentity, [iframeIdentity]);
  const iframeOrigin = useMemo(() => {
    if (!src) return null;

    try {
      return new URL(src).origin;
    } catch {
      return null;
    }
  }, [src]);

  const postWidgetVisibility = useCallback(
    (visibility) => {
      const targetWindow = iframeRef.current?.contentWindow;
      if (!targetWindow || !iframeOrigin || !isCommittedIframeIdentity() || !viewerHandshakeRef.current?.isReady()) return;

      targetWindow.postMessage(
        {
          type: 'output.widget.visibility',
          visibility,
        },
        iframeOrigin,
      );
    },
    [iframeOrigin, isCommittedIframeIdentity],
  );

  // Read the persisted collapse pref only on mount / widget change — never on a runtime
  // toggle — so toggling collapse never rewrites `src` and reloads the iframe.
  const iframeSrc = useMemo(() => {
    if (!src) return src;
    return readCollapsedPref(widgetId) ? appendCollapsedHash(src) : src;
  }, [src, widgetId]);

  const handleLoad = useCallback(() => setLoadedSrc(src), [src]);

  const postViewerSession = useCallback(
    (viewerSession) => {
      const targetWindow = iframeRef.current?.contentWindow;
      if (!targetWindow || !iframeOrigin || !isCommittedIframeIdentity() || !viewerSession) return;

      targetWindow.postMessage(
        {
          type: 'output.widget.viewer_session',
          nonce: viewerSession.nonce,
          viewer: viewerSession.viewer,
          expires_at: viewerSession.expires_at,
        },
        iframeOrigin,
      );
    },
    [iframeOrigin, isCommittedIframeIdentity],
  );

  const postViewerSessionRevoked = useCallback(
    (reason) => {
      const targetWindow = iframeRef.current?.contentWindow;
      if (!targetWindow || !iframeOrigin || !isCommittedIframeIdentity()) return;

      targetWindow.postMessage(
        {
          type: 'output.widget.viewer_session_revoked',
          reason,
        },
        iframeOrigin,
      );
    },
    [iframeOrigin, isCommittedIframeIdentity],
  );

  const postViewerUnavailable = useCallback(
    (reason) => {
      const targetWindow = iframeRef.current?.contentWindow;
      if (!targetWindow || !iframeOrigin || !isCommittedIframeIdentity() || !reason) return;

      targetWindow.postMessage(
        {
          type: 'output.widget.viewer_unavailable',
          reason,
        },
        iframeOrigin,
      );
    },
    [iframeOrigin, isCommittedIframeIdentity],
  );

  const postViewerHello = useCallback(() => {
    const targetWindow = iframeRef.current?.contentWindow;
    if (!targetWindow || !iframeOrigin || !isCommittedIframeIdentity()) return;

    targetWindow.postMessage({ type: 'output.widget.hello' }, iframeOrigin);
  }, [iframeOrigin, isCommittedIframeIdentity]);

  const postParentActions = useCallback(() => {
    const targetWindow = iframeRef.current?.contentWindow;
    if (
      !targetWindow ||
      !iframeOrigin ||
      !isCommittedIframeIdentity() ||
      !viewerHandshakeRef.current?.isReady() ||
      !shouldPostParentActions
    )
      return;

    targetWindow.postMessage(
      {
        type: 'output.widget.parent_actions',
        can_move_up: canMoveUp,
        can_move_down: canMoveDown,
      },
      iframeOrigin,
    );
  }, [canMoveDown, canMoveUp, iframeOrigin, isCommittedIframeIdentity, shouldPostParentActions]);

  const fetchViewerSession = useCallback(
    async (signal) => {
      const requestedAt = Date.now();
      let viewerSession;
      if (viewerSessionFetcher) {
        viewerSession = await viewerSessionFetcher(signal);
      } else {
        const response = await apiPost(`/widgets/${widgetId}/viewer_session`, {}, { signal });
        if (!response.ok) throw Object.assign(new Error('viewer_session_fetch_failed'), { status: response.status });

        const body = await response.json();
        viewerSession = body?.data?.viewer_session || null;
      }
      if (!viewerSession) return null;

      const expiresInSeconds = Number(viewerSession.expires_in);
      const expiresAtSeconds = Number(viewerSession.expires_at);
      const ttlMs =
        Number.isFinite(expiresInSeconds) && expiresInSeconds > 0 ? expiresInSeconds * 1000 : expiresAtSeconds * 1000 - requestedAt;

      return {
        ...viewerSession,
        refresh_at_ms: requestedAt + Math.max(0, ttlMs - VIEWER_SESSION_REFRESH_SKEW_MS),
      };
    },
    [viewerSessionFetcher, widgetId],
  );

  const viewerHandshakeCallbacks = useMemo(
    () => ({
      fetchSession: fetchViewerSession,
      postSession: postViewerSession,
      postUnavailable: postViewerUnavailable,
      postRevoked: postViewerSessionRevoked,
      postHello: postViewerHello,
      onError: logViewerSessionFetchFailure,
    }),
    [fetchViewerSession, postViewerHello, postViewerSession, postViewerSessionRevoked, postViewerUnavailable],
  );

  if (!viewerHandshakeRef.current) viewerHandshakeRef.current = createViewerHandshake(viewerHandshakeCallbacks);

  // Only committed callbacks may cross the iframe trust boundary. In particular, a render React
  // discards must not move the controller to an origin or widget id that was never committed.
  useLayoutEffect(() => {
    viewerHandshakeRef.current.updateCallbacks(viewerHandshakeCallbacks);
  }, [viewerHandshakeCallbacks]);

  // Activity can leave the iframe DOM running while every Effect is inactive. Reset protocol state
  // in the first commit phase that observes a new identity — a widget id change alone re-keys the
  // trust boundary even when the document stays put.
  useLayoutEffect(() => {
    if (committedIframeIdentityRef.current === iframeIdentity) return;

    committedIframeIdentityRef.current = iframeIdentity;
    viewerHandshakeRef.current.reset();
  }, [iframeIdentity]);

  // Reseed the placeholder height when the document changes; the new document posts its
  // real height after load. Keyed by src alone — a widget-id change with the document in
  // place must not clobber a live `output.widget.resize` height.
  useLayoutEffect(() => {
    if (committedSrcRef.current === src) return;

    committedSrcRef.current = src;
    setEmbedHeight(seedEmbedHeight(latestHeightRef.current, readCollapsedPref(widgetId)));
  }, [src, widgetId]);

  const viewerHandshakeMode = shouldFetchViewerSession ? 'session' : shouldPostViewerUnavailable ? 'unavailable' : 'silent';

  useEffect(() => {
    const controller = viewerHandshakeRef.current;
    if (!widgetId || !src || !iframeOrigin || shouldBlockEmbed) {
      controller.stop();
      return undefined;
    }

    controller.start({
      mode: viewerHandshakeMode,
      unavailableReason: shouldPostViewerUnavailable ? viewerUnavailableReason : null,
    });
    return () => controller.stop();
  }, [iframeOrigin, shouldBlockEmbed, shouldPostViewerUnavailable, src, viewerHandshakeMode, viewerUnavailableReason, widgetId]);

  useEffect(() => {
    if (viewerHandshakeMode !== 'session') return undefined;

    const pulseViewerHandshake = () => viewerHandshakeRef.current.pulse();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') pulseViewerHandshake();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', pulseViewerHandshake);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', pulseViewerHandshake);
    };
  }, [viewerHandshakeMode]);

  useLayoutEffect(
    function postWidgetVisibilityOnActivityChange() {
      postWidgetVisibility('visible');
      return function postWidgetHidden() {
        postWidgetVisibility('hidden');
      };
    },
    [postWidgetVisibility],
  );

  const handleWindowMessage = useCallback(
    (event) => {
      const targetWindow = iframeRef.current?.contentWindow;
      if (
        !targetWindow ||
        event.source !== targetWindow ||
        event.origin !== iframeOrigin ||
        committedIframeIdentityRef.current !== iframeIdentity
      )
        return;

      const messageType = event.data?.type;
      if (messageType === 'output.widget.ready') {
        viewerHandshakeRef.current.handleReady();
        postWidgetVisibility('visible');
        postParentActions();
        return;
      }

      if (messageType === 'output.widget.delete_request') {
        onDeleteRequest?.();
        return;
      }

      if (messageType === 'output.widget.move_request') {
        const direction = event.data?.direction;
        if (direction !== 'up' && direction !== 'down') return;

        onMoveRequest?.(direction);
        return;
      }

      if (messageType === 'output.widget.title') {
        storeWidgetRuntimeTitle({ workspaceId, widgetId, title: event.data?.title }).catch((error) => {
          console.warn('widget runtime title write failed', error);
        });
        return;
      }

      if (messageType === 'output.widget.open_direct_message_request') {
        const profileId = typeof event.data?.profile_id === 'string' ? event.data.profile_id.trim() : '';
        if (!profileId || !workspaceId || viewerUnavailableReason || directMessageRequestRef.current) return;

        directMessageRequestRef.current = profileId;
        openDirectMessageForWorkspaceProfile({ workspaceId: String(workspaceId), profileId })
          .catch((error) => {
            console.warn('widget direct message request failed', error);
          })
          .finally(() => {
            directMessageRequestRef.current = null;
          });
        return;
      }

      if (messageType === 'output.widget.resize') {
        // Edit mode pins the tile: zoomed tiles skew the iframe's self-measurement
        // (viewport-sized widgets would inflate on every round trip), so resize
        // messages are ignored until edit mode ends.
        const nextHeight = lockHeightRef.current ? null : clampSafeEmbedHeight(event.data?.height);
        if (nextHeight !== null) setEmbedHeight(nextHeight);
        if (typeof event.data?.collapsed === 'boolean') writeCollapsedPref(widgetId, event.data.collapsed);
        return;
      }

      if (messageType === 'output.widget.viewer_session_refresh') viewerHandshakeRef.current.handleRefreshRequest();
    },
    [
      iframeIdentity,
      iframeOrigin,
      onDeleteRequest,
      onMoveRequest,
      postParentActions,
      postWidgetVisibility,
      viewerUnavailableReason,
      widgetId,
      workspaceId,
    ],
  );

  // Refresh the indirection on every commit. The browser listener itself stays fixed, while
  // discarded renders never get a chance to publish callbacks or trust-boundary values.
  useLayoutEffect(() => {
    windowMessageHandlerRef.current = handleWindowMessage;
  });

  // Activity removes Effects but preserves the iframe DOM. Install this listener during the
  // synchronous commit phase and keep its browser identity fixed; the committed handler ref
  // supplies current iframe identity and callbacks without a cleanup/rebind window.
  useLayoutEffect(() => {
    const receiveWindowMessage = (event) => windowMessageHandlerRef.current?.(event);
    window.addEventListener('message', receiveWindowMessage);
    return () => window.removeEventListener('message', receiveWindowMessage);
  }, []);

  useEffect(() => {
    postParentActions();
  }, [postParentActions]);

  useEffect(() => {
    const activatePointerGuard = () => {
      if (isPointerGuardActiveRef.current) return;
      isPointerGuardActiveRef.current = true;
      setIsPointerGuardActive(true);
    };
    const deactivatePointerGuard = () => {
      if (!isPointerGuardActiveRef.current) return;
      isPointerGuardActiveRef.current = false;
      setIsPointerGuardActive(false);
    };

    document.addEventListener('pointerdown', activatePointerGuard, true);
    window.addEventListener('pointerup', deactivatePointerGuard);
    window.addEventListener('pointercancel', deactivatePointerGuard);
    window.addEventListener('blur', deactivatePointerGuard);

    return () => {
      document.removeEventListener('pointerdown', activatePointerGuard, true);
      window.removeEventListener('pointerup', deactivatePointerGuard);
      window.removeEventListener('pointercancel', deactivatePointerGuard);
      window.removeEventListener('blur', deactivatePointerGuard);
    };
  }, []);

  if (!src) return null;

  // Hard-block path: `requires_viewer` widgets that can't degrade gracefully (checkout
  // flows, profile editors, etc.) get a generic Output-owned placeholder *instead of*
  // the iframe. The iframe never mounts, so no handshake / fetch / postMessage occurs
  // and the widget author doesn't need to author an anonymous-state UI for this case.
  // Contrast with viewer-capable-but-not-required widgets, which DO mount and receive
  // `viewer_unavailable` so the widget renders its own logged-out state.
  if (shouldBlockEmbed) {
    return (
      <div
        data-debug-agent-chat-id={agentChatId}
        data-debug-widget-id={widgetId}
        data-widget-blocked-reason={viewerUnavailableReason}
        className={cn('relative w-full overflow-hidden', className)}
        style={{ height: embedHeight }}
      >
        {/*
          Neutral copy by design. Anonymous shadow viewers and signed-in-but-non-member
          shadows hit the same `viewerUnavailableReason="shadow"` branch, so we can't
          assume the right call-to-action is "sign in" (the latter user is already
          signed in and would need to "join" instead). A generic blocked state stays
          accurate for both cases until/unless a per-reason CTA gets designed in.
        */}
        <div className="flex size-full items-center justify-center bg-gray-50 px-4 text-center text-sm text-gray-500 dark:bg-neutral-950 dark:text-neutral-400">
          This widget isn&rsquo;t available here.
        </div>
      </div>
    );
  }

  return (
    <div
      data-debug-agent-chat-id={agentChatId}
      data-debug-widget-id={widgetId}
      className={cn('relative w-full overflow-hidden', className)}
      style={{ height: embedHeight }}
    >
      {!isLoaded ? (
        <div className="absolute inset-0 z-1 flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
          <SpinnerIcon className="size-4 animate-spin text-gray-500 dark:text-neutral-400" />
        </div>
      ) : null}
      <iframe
        ref={iframeRef}
        title={title}
        src={iframeSrc}
        sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-top-navigation-by-user-activation"
        // Permissions Policy does not inherit across origins: without an explicit
        // delegation the widget origin cannot call navigator.geolocation at all, and
        // the failure looks identical to the user denying the prompt. The browser
        // still asks the user before any widget sees a coordinate.
        allow="autoplay; encrypted-media; clipboard-write; geolocation"
        scrolling="no"
        onLoad={handleLoad}
        className={cn('block size-full border-0', !isLoaded && 'opacity-0')}
      />
      {isPointerGuardActive ? (
        // HACK ALERT: iframe embeds are separate browsing contexts, so dragging a
        // parent surface across one can steal pointermove/pointerup from resize,
        // reorder, or horizontal-scroll gestures. This transparent shield only
        // appears after a parent-document pointerdown; pointerdowns that start in
        // the iframe never reach this document, so normal widget interaction stays
        // available when the user intentionally clicks the embed.
        <div aria-hidden="true" className="absolute inset-0 z-2 bg-transparent" />
      ) : null}
    </div>
  );
});

export default WidgetEmbed;
